<template>
	<div>
		<PageHeader
			:data="{
				name: campaign.name,
				breadcrumbs: [
					{ name: 'Home', to: '/' },
					{ name: 'Campaigns', to: '/campaigns' },
				],
			}"
		/>

		<div v-if="loadingCampaign">
			<div class="d-flex justify-content-center">
				<div class="spinner-border" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>
		</div>
		<div v-else>
			<!-- Campaign - SCHEDULED -->
			<div v-if="campaign.status == 'sending'" class="row">
				<div class="col-md-12 mb-3">
					<div class="col-12 text-center">
						<h2>Campaign is in process of sending</h2>
					</div>
					<div class="col-12 text-center">
						<h3>Sent {{ sentDeliveries }} of {{ campaign.municipalities_count }}</h3>
					</div>
					<div v-if="loadingCampaign" class="col-12 text-center">
						<div class="d-flex justify-content-center">
							<div class="spinner-border me-3" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
					</div>
				</div>
				<div class="col-md-3"></div>
				<div class="col-md-6 text-center">
					<div class="alert alert-warning bg-warning-lighter text-dark mb-3 mt-2">
						💡 Deliveries are being sent to LOB, and each delivery has a time window of 20 minutes during which it can be canceled. If, for any reason, the campaign
						needs to be stopped, you can press the "Abort" button to halt the campaign. The "Abort" button will instantly cease the remaining Delivery Objects from
						being sent, canceling all deliveries that are within the time window for cancellation. Unfortunately, if a delivery is outside the cancellation window, we
						regrettably cannot take any action in that regard. 🤷‍♂️
					</div>
					<button @click="abbortCampaign()" class="btn btn-danger btn-lg rounded">
						<font-awesome-icon v-if="loadingAbbort" :icon="['fas', 'spinner']" class="fa-spin" /> ✋ Abbort!
					</button>
				</div>

				<div class="col-md-12 my-5">
					<!-- Delivery Objects -->
					<div class="col-md-12 mb-3">
						<div class="delivery-objects">
							<div class="d-flex justify-content-between align-items-center">
								<div>
									<div class="d-flex justify-content-between align-items-center mb-2">
										<div>
											<small
												>Initial Source: <span class="text-capitalize">{{ campaign.source }}</span></small
											>
											<h4 class="mb-2 d-flex justify-content-start align-items-center">
												Delivery Objects <span class="badge bg-primary ms-2">{{ campaign.municipalities_count }}</span>
											</h4>
										</div>
									</div>
								</div>
							</div>

							<table :class="!loading && deliveryObjects.length > 0 ? 'table table-hover' : 'table table-hover loading'">
								<thead>
									<tr>
										<th class="text-center">ID</th>
										<th>Name</th>
										<th>City</th>
										<th>Address</th>
										<th>Zip</th>
										<th class="text-center">State</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>
									<template v-for="dObject in deliveryObjects">
										<tr
											:key="'edit-' + dObject.id"
											:class="{
												'bg-danger-light':
													(dObject.city == '' || dObject.city == null) &&
													(dObject.address == '' || dObject.address == null) &&
													(dObject.zip == '' || dObject.zip == null),
												'bg-warning-light':
													dObject.city != '' &&
													dObject.city != null &&
													(dObject.address == '' || dObject.address == null) &&
													(dObject.zip == '' || dObject.zip == null),
												'bg-warning-lighter':
													(dObject.city == '' || dObject.city == null) &&
													dObject.address != '' &&
													dObject.address != null &&
													dObject.zip != '' &&
													dObject.zip != null,
											}"
										>
											<td class="text-center align-middle">
												{{ dObject.id }}
											</td>
											<td class="align-middle">
												<h6 class="p-0 m-0 d-flex justify-content-start align-items-center">
													{{ dObject.name }}
													<span
														v-if="
															(dObject.city == '' || dObject.city == null) &&
																(dObject.address == '' || dObject.address == null) &&
																(dObject.zip == '' || dObject.zip == null)
														"
														class="btn btn-sm ms-2"
														title="A lot of data is missing for this Delivery Object. The delivery will not be sent to this delivery object for sure."
													>
														<font-awesome-icon :class="['text-danger fs-4']" :icon="['fas', 'exclamation-triangle']" />
													</span>
													<span
														class="btn btn-sm ms-2"
														title="Important data is missing for this Object Delivery. The delivery will not be sent to this delivery object for sure."
														v-else-if="(dObject.address == '' || dObject.address == null) && (dObject.zip == '' || dObject.zip == null)"
													>
														<font-awesome-icon :class="['text-warning fs-4 ms-2']" :icon="['fas', 'exclamation-circle']" />
													</span>
													<span
														class="btn btn-sm ms-2"
														title="The city is missing; it's nice to have but not mandatory if all other data is available."
														v-else-if="dObject.city == '' || dObject.city == null"
													>
														<font-awesome-icon :class="['text-warning fs-6 ms-2']" :icon="['fas', 'exclamation']" />
													</span>
												</h6>
											</td>
											<td class="align-middle">{{ dObject.city }}</td>
											<td class="align-middle">{{ dObject.address }}</td>
											<td class="align-middle">{{ dObject.zip }}</td>
											<td class="text-center align-middle">{{ dObject.state }}</td>
											<td class="text-center">
												<div v-if="!dObject.delivery" class="spinner-border spinner-border-sm" role="status">
													<span class="visually-hidden">Loading...</span>
												</div>
												<div v-else>
													<span v-if="dObject.delivery.status == 'sent'" class="badge bg-success">Sent</span>
													<span v-if="dObject.delivery.status == 'error'" :title="dObject.delivery.message" class="badge bg-danger">Error</span>
												</div>
											</td>
										</tr>
									</template>
								</tbody>
							</table>
							<div v-if="loading && deliveryObjects.length === 0" class="d-flex justify-content-center">
								<div class="spinner-border" role="status">
									<span class="visually-hidden">Loading...</span>
								</div>
							</div>
							<LPDataTablePagination :pagination="pagination" v-on:getItems="getItems($event.page)" />
						</div>
					</div>
				</div>
			</div>
			<!-- #Campaign - SCHEDULED -->
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { handleErrors } from '../../actions/helpers'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'

export default {
	name: 'CampaignSending',
	components: { PageHeader, LPDataTablePagination },
	data() {
		return {
			loading: false,
			loadingCampaign: false,
			loadingAbbort: false,
			campaign: {},
			params: {},
			pagination: {},
			page: this.$route.query.page || 1,
			deliveryObjects: [],
			sentDeliveries: 0,
			intervalId: null,
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
		totalSentDeliveryObjects() {
			return this.deliveryObjectsCount - this.deliveryErrorsCount - this.deliveryCanceledCount
		},
	},
	mounted: function() {
		this.intervalId = setInterval(() => {
			console.log('Ovo je iz Sending!')
			if (this.campaign.status == 'sending') {
				this.getDeliveryObjects()
				this.getCampaignDeliveriesCount()
			}
		}, 10000)
	},
	beforeDestroy() {
		// Stop the interval when the component is about to be destroyed
		clearInterval(this.intervalId)
	},
	created() {
		this.getCampaign()
		this.getCampaignDeliveriesCount()
	},
	methods: {
		/**
		 * Get Campaign
		 * Function get selected campaign data
		 */
		getCampaign() {
			this.loadingCampaign = true
			restApi
				.get(`/campaigns/${this.$route.params.id}`)
				.then(({ data }) => {
					// If campaign status is "sent"
					if (data.status == 'sent') {
						this.$router.push(`/campaigns/edit/${this.$route.params.id}`)
					}
					// If campaign status is "canceled"
					if (data.status == 'canceled') {
						this.$router.push(`/campaigns/edit/${this.$route.params.id}`)
					}
					this.printingFilters = []
					data.filters = JSON.parse(data.filters)
					this.campaign = data
					this.localParams = data.filters
					this.getDeliveryObjects()
					for (const property in this.localParams) {
						if (property !== 'page' && property !== 'orderBy' && property !== 'direction' && property !== 'perPage') {
							let value = this.localParams[property]
							if (value) {
								value === 'Yes'
							}
							if (!value) {
								value === 'No'
							}
							this.printingFilters.push(`${property}: ${value}`)
						}
					}
					this.loadingCampaign = false
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Get Delivery Objects
		 * Function getting all Delivery Objects for current campaign
		 */
		getDeliveryObjects() {
			this.loading = true
			this.params = {
				page: this.page,
				search: this.deliveryObjectSearch,
			}
			restApi
				.get(`/campaigns/${this.$route.params.id}/delivery-objects`, { params: this.params })
				.then(({ data }) => {
					this.deliveryObjects = data.data
					this.pagination = data
					delete this.pagination.data
					this.loading = false
					//this.$router.push({ path: this.$router.currentPath, query: this.params })
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Get items from page that returned from LPDataTablePagination
		getItems(page) {
			this.page = page
			this.getDeliveryObjects()
		},

		/**
		 * Abbort Campaign
		 * Function sends signal to backend to stop sending deliveries
		 */
		abbortCampaign() {
			this.$swal
				.fire({
					title: 'Are you sure want to abbort this campaign?',
					text: `All delivery objects that are sent will be tried to cancel and all not sent will be canceled`,
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, Abbort!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						clearInterval(this.intervalId)
						this.loadingAbbort = true
						restApi
							.post(`/campaigns/${this.$route.params.id}/abbort`)
							.then(({ data }) => {
								console.log(data)
								this.$router.push(`/campaigns/edit/${this.$route.params.id}`)
								this.loadingAbbort = false
							})
							.catch(error => {
								this.$swal.fire({
									icon: 'error',
									title: handleErrors(error, ''),
									timer: 6000,
								})
							})
					}
				})
		},

		/**
		 * Get Campaign Deliveries Count
		 * Function returs number of sent deliveries
		 */
		getCampaignDeliveriesCount() {
			this.params = {
				campaign_id: this.$route.params.id,
			}
			restApi
				.get('/deliveries/count', { params: this.params })
				.then(({ data }) => {
					this.sentDeliveries = data
					if (data == this.campaign.municipalities_count) {
						this.getCampaign()
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
	},

	watch: {
		sentDeliveries: function() {
			if (this.sentDeliveries == this.campaign.municipalities_count) {
				console.log(this.sentDeliveries)
				//this.getCampaign()
			}
		},
	},
}
</script>
